import React, { useEffect, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Container,
  Fade,
  Grid,
  Typography,
  useTheme,
  Skeleton
} from "@mui/material";
import {
  TrendingUp,
  MoneyOff,
  AttachMoney,
  Add,
  Assessment,
  Category,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ModernCard, StatsCard } from "../common/StyledComponents";
import { 
  EXPENSES_ROUTE_PATH, 
  INCOMES_ROUTE_PATH, 
  MONTHLY_REPORTS_ROUTE_PATH, 
  CATEGORIES_ROUTE_PATH 
} from "../../utils/constants/AppConstants";

import { useExpensesInRange } from "../../hooks/expense";
import { useIncomesInRange } from "../../hooks/income";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import Expense from "../../models/finance/Expense";
import Income from "../../models/finance/Income";
import { useAuth } from "../../auth/AuthContext";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useQueryClient } from "@tanstack/react-query";
import { getCategories } from "../../api/category";
import { CategoryType } from "../../models/finance/CategoryType";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const LoggedInHomePageComponent: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { error, helpText, isLoading, clearError } = useError();
  const { startOfMonth, endOfMonth } = useMemo(() => {
    const now = new Date();
    return {
      startOfMonth: new Date(now.getFullYear(), now.getMonth(), 1),
      endOfMonth: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    };
  }, []);
  const { userProfile } = useUserProfileContext();
  const { data: expensesData } = useExpensesInRange(startOfMonth, endOfMonth);
  const { data: incomesData } = useIncomesInRange(startOfMonth, endOfMonth);

  // Extract first name and limit length for welcome message
  const userName = userProfile?.name || 'User';
  const firstName = userName.split(' ')[0];
  const displayName = firstName.length > 15 ? firstName.substring(0, 15) + '...' : firstName;

  const recentExpenses = useMemo<Expense[]>(() => {
    const list = expensesData || [];
    return [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  }, [expensesData]);
  const recentIncomes = useMemo<Income[]>(() => {
    const list = incomesData || [];
    return [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  }, [incomesData]);
  const totalExpenses = useMemo(() => (expensesData || []).reduce((sum, e) => sum + e.amount, 0), [expensesData]);
  const totalIncome = useMemo(() => (incomesData || []).reduce((sum, i) => sum + i.amount, 0), [incomesData]);

  // Prefetch common data for snappier navigation
  useEffect(() => {
    if (!isAuthenticated) return;
    queryClient.prefetchQuery({
      queryKey: ["categories", CategoryType.Expense, false],
      queryFn: () => getCategories(CategoryType.Expense, false),
      staleTime: 60_000,
    });
    queryClient.prefetchQuery({
      queryKey: ["categories", CategoryType.Income, false],
      queryFn: () => getCategories(CategoryType.Income, false),
      staleTime: 60_000,
    });
  }, [isAuthenticated, queryClient]);

  const netBalance = totalIncome - totalExpenses;
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Early return if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Fade in={true} timeout={1000}>
          <Box>
            {/* Header Skeleton */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 6 }}>
              <Skeleton variant="circular" width={80} height={80} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={300} height={40} />
                <Skeleton variant="text" width={200} height={24} />
              </Box>
            </Box>

            {/* Stats Cards Skeleton */}
            <Grid container spacing={3} sx={{ mb: 6 }}>
              {[...Array(3)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>

            {/* Lists Skeleton */}
            <Grid container spacing={4}>
              {[...Array(2)].map((_, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <ErrorAlert
          error={error}
          helpText={helpText}
          onClear={clearError}
          onRetry={() => queryClient.invalidateQueries()}
          title="Dashboard Error"
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
      <Fade in={true} timeout={1000}>
        <Box>
          {/* Welcome Header */}
          <Box sx={{ 
            mb: { xs: 3, sm: 5, md: 6 },
            textAlign: { xs: 'center', sm: 'left' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1.5, sm: 3 },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              mb: { xs: 1.5, sm: 2 },
            }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 70, md: 80 },
                  height: { xs: 48, sm: 70, md: 80 },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
                  fontWeight: 700,
                }}
              >
                {userProfile?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Welcome back, {displayName}!
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                fontWeight: 500,
              }}
            >
              {currentMonth} Overview
            </Typography>
          </Box>

          {/* Financial Stats */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
            {/* Total Expenses */}
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.error.light}20 0%, ${theme.palette.error.main}20 100%)`,
                  border: `1px solid ${theme.palette.error.light}40`,
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                  <MoneyOff sx={{ fontSize: { xs: 36, sm: 48 }, color: theme.palette.error.main }} />
                </Box>
                <Typography variant="h6" color="error.main" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Total Expenses
                </Typography>
                <Typography variant="h3" fontWeight="bold" color="error.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}>
                  {formatCurrency(totalExpenses)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This month
                </Typography>
              </StatsCard>
            </Grid>

            {/* Total Income */}
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.success.light}20 0%, ${theme.palette.success.main}20 100%)`,
                  border: `1px solid ${theme.palette.success.light}40`,
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                  <AttachMoney sx={{ fontSize: { xs: 36, sm: 48 }, color: theme.palette.success.main }} />
                </Box>
                <Typography variant="h6" color="success.main" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Total Income
                </Typography>
                <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}>
                  {formatCurrency(totalIncome)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This month
                </Typography>
              </StatsCard>
            </Grid>

            {/* Net Balance */}
            <Grid item xs={12} sm={12} md={4}>
              <StatsCard
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}20 100%)`,
                  border: `1px solid ${theme.palette.primary.light}40`,
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 2 } }}>
                  <TrendingUp sx={{ fontSize: { xs: 36, sm: 48 }, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" color="primary.main" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Net Balance
                </Typography>
                <Typography 
                  variant="h3" 
                  fontWeight="bold" 
                  color={netBalance >= 0 ? "success.main" : "error.main"}
                  sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}
                >
                  {netBalance >= 0 ? '+' : ''}{formatCurrency(netBalance)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This month
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Recent Transactions */}
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
            {/* Recent Expenses */}
            <Grid item xs={12} md={6}>
              <ModernCard sx={{ height: '100%' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography
                    variant="h5"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                  >
                    <MoneyOff color="error" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Recent Expenses
                  </Typography>
                </Box>
                
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {recentExpenses.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No expenses recorded this month
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => navigate(EXPENSES_ROUTE_PATH)}
                        sx={{ mt: 2 }}
                      >
                        Add Expense
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {recentExpenses.map((expense, index) => (
                        <Box key={expense.id} sx={{ mb: index < recentExpenses.length - 1 ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {expense.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(expense.date)} • {expense.category}
                              </Typography>
                            </Box>
                            <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                              -{formatCurrency(expense.amount)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      <Button
                        variant="text"
                        onClick={() => navigate(EXPENSES_ROUTE_PATH)}
                        sx={{ mt: 2, width: '100%' }}
                      >
                        View All Expenses
                      </Button>
                    </>
                  )}
                </Box>
              </ModernCard>
            </Grid>

            {/* Recent Income */}
            <Grid item xs={12} md={6}>
              <ModernCard sx={{ height: '100%' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography
                    variant="h5"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                  >
                    <AttachMoney color="success" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Recent Income
                  </Typography>
                </Box>
                
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {recentIncomes.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No income recorded this month
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => navigate(INCOMES_ROUTE_PATH)}
                        sx={{ mt: 2 }}
                      >
                        Add Income
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {recentIncomes.map((income, index) => (
                        <Box key={income.id} sx={{ mb: index < recentIncomes.length - 1 ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {income.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(income.date)} • {income.category}
                              </Typography>
                            </Box>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                              +{formatCurrency(income.amount)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      <Button
                        variant="text"
                        onClick={() => navigate(INCOMES_ROUTE_PATH)}
                        sx={{ mt: 2, width: '100%' }}
                      >
                        View All Income
                      </Button>
                    </>
                  )}
                </Box>
              </ModernCard>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, sm: 6 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <ModernCard
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { transform: 'translateY(-4px)' },
                  transition: 'all 0.3s ease-in-out',
                }}
                onClick={() => navigate(EXPENSES_ROUTE_PATH)}
              >
                <MoneyOff sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.error.main, mb: { xs: 1, sm: 2 } }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Manage Expenses
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track your spending
                </Typography>
              </ModernCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ModernCard
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { transform: 'translateY(-4px)' },
                  transition: 'all 0.3s ease-in-out',
                }}
                onClick={() => navigate(INCOMES_ROUTE_PATH)}
              >
                <AttachMoney sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.success.main, mb: { xs: 1, sm: 2 } }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Manage Income
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Record your earnings
                </Typography>
              </ModernCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ModernCard
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { transform: 'translateY(-4px)' },
                  transition: 'all 0.3s ease-in-out',
                }}
                onClick={() => navigate(MONTHLY_REPORTS_ROUTE_PATH)}
              >
                <Assessment sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.primary.main, mb: { xs: 1, sm: 2 } }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  View Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Analyze your finances
                </Typography>
              </ModernCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ModernCard
                sx={{
                  p: { xs: 2, sm: 3 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { transform: 'translateY(-4px)' },
                  transition: 'all 0.3s ease-in-out',
                }}
                onClick={() => navigate(CATEGORIES_ROUTE_PATH)}
              >
                <Category sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.info.main, mb: { xs: 1, sm: 2 } }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Categories
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organize transactions
                </Typography>
              </ModernCard>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Container>
  );
};

export default LoggedInHomePageComponent;
